import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, ArrowRight, ShieldCheck } from "lucide-react";

import Logo from "src/components/common/Logo";
import Input from "src/components/ui/Input";
import Button from "src/components/ui/Button";
import AuthBrandPanel from "src/components/auth/AuthBrandPanel";
import AuthThemeToggle from "src/components/auth/AuthThemeToggle";

import { getFromStorage, setToStorage } from "src/utils/storage";
import storageKeys from "src/constants/storage";
import * as authActions from "src/redux/slices/authAdminSlice";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";

const LoginPage: React.FC = () => {
  const [apiError, setApiError] = useState("");

  const navigate = useNavigate();

  const dispatch = useAppDispatch();
  const { login } = useAppSelector((state) => state.authAdmin);

  useEffect(() => {
    const authAdminToken = getFromStorage(storageKeys.authAdminToken);
    if (authAdminToken) {
      navigate("/admin/dashboard");
    }
  }, []);

  useEffect(() => {
    if (login?.data?.token) {
      setToStorage(storageKeys.authAdminToken, login.data.token);
      dispatch(authActions.getUserDetailsRequest());
      navigate("/admin/dashboard");
    } else if (login?.error) {
      setApiError(login.error);
    }
  }, [login, navigate]);

  const handleSubmit = (data: { email: string; password: string }) => {
    setApiError("");
    dispatch(authActions.loginRequest(data));
  };

  return (
    <div className="min-h-screen flex bg-gray-50 dark:bg-gray-900">
      <AuthThemeToggle />
      <AuthBrandPanel
        badge="Admin console"
        heading="Invyntra Control Room"
        subtext="Review tenant health, activate plans, and resolve escalations with full visibility across the platform."
      >
        <div className="grid grid-cols-2 gap-2.5">
          {[
            "Platform revenue insights",
            "Tenant lifecycle actions",
            "Risk & compliance alerts",
            "Support prioritization",
          ].map((line) => (
            <div
              key={line}
              className="rounded-xl border border-white/15 bg-white/10 p-3 text-[12px] text-white/85 backdrop-blur"
            >
              {line}
            </div>
          ))}
        </div>
      </AuthBrandPanel>

      {/* Form panel */}
      <div className="flex flex-1 items-center justify-center p-6">
        <div className="w-full max-w-[380px]">
          <div className="rounded-[18px] border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800">
            <div className="mb-5 flex items-center gap-2">
              <Logo size="small" variant="contained" />
              <Logo size="large" variant="text" />
            </div>

            <h2 className="mb-1.5 text-xl font-extrabold text-gray-900 dark:text-gray-100">
              Sign in to Admin
            </h2>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              Use your Invyntra internal credentials to continue.
            </p>

            {apiError && (
              <div className="mb-4 rounded-lg bg-red-100 p-2.5 text-center text-sm text-red-700 dark:bg-red-900/30 dark:text-red-400">
                {apiError}
              </div>
            )}

            <LoginFields onSubmit={handleSubmit} loading={login?.loading} />

            <div className="mt-5 flex items-center justify-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
              <ShieldCheck size={12} />
              Internal access only
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const LoginFields: React.FC<{
  onSubmit: (data: { email: string; password: string }) => void;
  loading?: boolean;
}> = ({ onSubmit, loading }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  return (
    <form
      className="space-y-3.5"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({ email, password });
      }}
    >
      <Input
        label="Email"
        type="email"
        placeholder="you@invyntra.in"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        startAdornment={<Mail size={16} />}
        required
      />
      <Input
        label="Password"
        type={showPassword ? "text" : "password"}
        placeholder="••••••••••"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        startAdornment={<Lock size={16} />}
        endAdornment={
          <button
            type="button"
            onClick={() => setShowPassword((s) => !s)}
            className="flex items-center"
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        }
        required
      />
      <Button type="submit" fullWidth isLoading={loading} className="mt-1.5">
        Sign in
        <ArrowRight size={15} className="ml-1.5" />
      </Button>
    </form>
  );
};

export default LoginPage;
